import React, { useEffect, useState } from "react";
import apiDefinitions from "../../../core/api/apiDefinitions";
import { showError } from "../../../core/alert/sweetAlertConfig";

interface JobCardSummary {
  date: string;
  pending: number;
  ongoing: number;
  completed: number;
}

const JobCard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<JobCardSummary | null>(null);

  const fetchJobCardSummary = async () => {
    setLoading(true);
    try {
      const response = await apiDefinitions.getDailyJobCardSummary();
      if (response?.data?.status === "S1000") {
        const summary =
          response.data.data?.dailySummaryData?.length > 0
            ? response.data.data.dailySummaryData[0]
            : null;
        setData(summary);
      } else {
        showError(
          "Failed to fetch job card summary: " +
            (response?.data?.statusDescription || "Unknown error")
        );
      }
    } catch (error: any) {
      showError(
        "Failed to fetch job card summary: " +
          (error.response?.data?.statusDescription || "Network error")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobCardSummary();
  }, []);

  return (
    <div className="rm-glass-card rm-glass-card-interactive flex-fill w-100 p-3 p-md-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3 mb-3 border-bottom border-light">
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "42px",
              height: "42px",
              background: "rgba(139, 92, 246, 0.12)",
              color: "var(--rm-accent-purple)",
              fontSize: "1.25rem",
            }}
          >
            <i className="ti ti-briefcase" />
          </div>
          <div>
            <h5 className="m-0 fw-bold" style={{ color: "var(--rm-text-primary)" }}>
              Daily Job Cards
            </h5>
            <small style={{ color: "var(--rm-text-muted)" }}>
              {loading
                ? "Refreshing..."
                : data?.date
                ? `Date: ${new Date(data.date).toLocaleDateString()}`
                : "Active maintenance overview"}
            </small>
          </div>
        </div>

        <button
          onClick={fetchJobCardSummary}
          disabled={loading}
          className="btn btn-sm btn-light rounded-pill px-3 shadow-sm border-0 d-inline-flex align-items-center gap-1"
          style={{ fontSize: "0.8rem", color: "var(--rm-text-secondary)" }}
          title="Refresh Data"
        >
          <i className={`ti ti-refresh ${loading ? "rotate" : ""}`} />
          <span className="d-none d-sm-inline">Refresh</span>
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-info mb-2" role="status" />
          <p className="small mb-0 text-muted">Loading job cards...</p>
        </div>
      ) : (
        <div className="row g-2 g-md-3">
          {/* Pending */}
          <div className="col-12 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-warning)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-warning)" }}
              >
                {data?.pending ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-text-secondary)", fontSize: "0.8rem", fontWeight: 600 }}>
                Pending Tasks
              </small>
            </div>
          </div>

          {/* Ongoing */}
          <div className="col-12 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-info)", background: "rgba(2, 132, 199, 0.05)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-info)" }}
              >
                {data?.ongoing ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-info)", fontSize: "0.8rem", fontWeight: 700 }}>
                In Progress
              </small>
            </div>
          </div>

          {/* Completed */}
          <div className="col-12 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-success)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-success)" }}
              >
                {data?.completed ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-text-secondary)", fontSize: "0.8rem", fontWeight: 600 }}>
                Completed
              </small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobCard;
